import React from "react";
import Section from "./Section";

export default function HTML_RGB() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🌈 HTML RGB and RGBA Colors</h1>

      {/* 🔹 Introduction */}
      <div
        style={{
          background: "linear-gradient(135deg, #fff7ed, #ffedd5)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
          marginBottom: "2rem",
        }}
      >
        <p className="tutorial-intro">
          The <strong>RGB color model</strong> defines colors using the
          intensity of <b>Red</b>, <b>Green</b>, and <b>Blue</b> — each ranging
          from 0 to 255. You can also add an <b>Alpha</b> value (RGBA) to
          control transparency.
        </p>
      </div>
      {/* Section 1️⃣ — Basic RGB Colors */}
      <Section
        title="1️⃣ Basic RGB Examples"
        html={`<div style="background-color: rgb(255, 0, 0); color: white; padding: 10px;">rgb(255, 0, 0) — Red</div>
<div style="background-color: rgb(0, 255, 0); color: black; padding: 10px;">rgb(0, 255, 0) — Green</div>
<div style="background-color: rgb(0, 0, 255); color: white; padding: 10px;">rgb(0, 0, 255) — Blue</div>`}
        description="In RGB, each component (R, G, B) is a number between 0 and 255. For example, (255, 0, 0) means full red with no green or blue."
      />

      {/* Section 2️⃣ — Mixing Colors */}
      <Section
        title="2️⃣ Mixing Colors"
        html={`<div style="background-color: rgb(255, 255, 0); padding: 10px;">rgb(255, 255, 0) — Yellow</div>
<div style="background-color: rgb(0, 255, 255); padding: 10px;">rgb(0, 255, 255) — Cyan</div>
<div style="background-color: rgb(255, 0, 255); padding: 10px;">rgb(255, 0, 255) — Magenta</div>
<div style="background-color: rgb(128, 128, 128); padding: 10px;">rgb(128, 128, 128) — Gray</div>`}
        description="By mixing different amounts of red, green, and blue, you can create millions of colors."
      />

      {/* Section 3️⃣ — Shades of Gray */}
      <Section
        title="3️⃣ Shades of Gray"
        html={`<div style="background-color: rgb(50, 50, 50); color: white; padding: 10px;">rgb(50, 50, 50)</div>
<div style="background-color: rgb(128, 128, 128); color: white; padding: 10px;">rgb(128, 128, 128)</div>
<div style="background-color: rgb(200, 200, 200); color: black; padding: 10px;">rgb(200, 200, 200)</div>`}
        description="If all three RGB values are the same, you get shades of gray — from black (0,0,0) to white (255,255,255)."
      />

      {/* Section 4️⃣ — RGBA with Transparency */}
      <Section
        title="4️⃣ RGBA (Adding Transparency)"
        html={`<div style="background-color: rgba(255, 0, 0, 0.1); padding: 10px;">rgba(255, 0, 0, 0.1)</div>
<div style="background-color: rgba(255, 0, 0, 0.5); padding: 10px;">rgba(255, 0, 0, 0.5)</div>
<div style="background-color: rgba(255, 0, 0, 0.9); padding: 10px;">rgba(255, 0, 0, 0.9)</div>`}
        description="RGBA adds an alpha channel that controls transparency. 1 is fully opaque, 0 is fully transparent."
      />

      {/* Section 5️⃣ — Background Overlay Demo */}
      <Section
        title="5️⃣ RGBA Overlay Example"
        html={`<div style="position: relative; background-image: url('https://images.unsplash.com/photo-1503264116251-35a269479413?auto=format&fit=crop&w=500&q=60'); background-size: cover; height: 200px; border-radius: 10px;">
  <div style="background-color: rgba(0, 0, 0, 0.4); height: 100%; display: flex; align-items: center; justify-content: center; color: white; font-size: 20px;">
    RGBA Overlay Example
  </div>
</div>`}
        description="RGBA is perfect for overlays — for example, applying semi-transparent black over an image for readable text."
      />

      {/* ✅ Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #fff7ed, #ffedd5)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary — RGB & RGBA Key Points</h2>
        <ul className="tutorial-list-points">
          <li>RGB = Red, Green, Blue → values range from 0–255.</li>
          <li>RGBA adds Alpha (opacity): 0 → transparent, 1 → solid.</li>
          <li>Mix colors to create new ones by adjusting each channel.</li>
          <li>Use RGBA for transparency and overlays.</li>
          <li>White = rgb(255,255,255), Black = rgb(0,0,0).</li>
        </ul>
      </div>
    </div>
  );
}
